import React from 'react';
import { Tooltip } from '../common/tooltip.component';
import './form-field.style.scss';

interface FormFieldProps {
  /** Label text for the field */
  label: string;
  /** Tooltip content to show for help */
  tooltipContent?: string;
  /** Position of the tooltip */
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** Whether the field is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Help text to display below the field */
  helpText?: string;
  /** Children components (input, select, etc.) */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Reusable FormField component with integrated tooltip
 * Follows SOLID principles with single responsibility for form field structure
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  tooltipContent,
  tooltipPosition = 'top',
  required = false,
  error,
  helpText,
  children,
  className = ''
}) => {
  return (
    <div className={`form-field ${className} ${error ? 'form-field--error' : ''}`}>
      <label className="form-field__label">
        {label}
        {required && <span className="form-field__required">*</span>}
        {tooltipContent && (
          <Tooltip content={tooltipContent} position={tooltipPosition}>
            <svg
              className='form-field__info-icon'
              xmlns="http://www.w3.org/2000/svg"
              width={14}
              height={14}
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M6.7002 9.10508V6.10508M6.7002 4.30508V4.29908M12.7002 6.69922C12.7002 10.0129 10.0139 12.6992 6.7002 12.6992C3.38649 12.6992 0.700195 10.0129 0.700195 6.69922C0.700195 3.38551 3.38649 0.699219 6.7002 0.699219C10.0139 0.699219 12.7002 3.38551 12.7002 6.69922Z"
                stroke="#6750B8"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>

          </Tooltip>
        )}
      </label>

      <div className="form-field__input-wrapper">
        {children}
      </div>

      {error && (
        <span className="form-field__error">{error}</span>
      )}

      {helpText && !error && (
        <p className="form-field__help-text">{helpText}</p>
      )}
    </div>
  );
};
